import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { navLinks } from './data/mock';
import HeroSection from './components/sections/HeroSection';
import TrustStrip from './components/sections/TrustStrip';
import {
  ProductsSection,
  TestimonialsSection,
  ClientsSection,
  CTASection,
} from './components/sections/ContentSections';
import AboutPage from './components/sections/AboutPage';
import Footer from './components/sections/Footer';

// Navbar
const Navbar = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isAboutPage = location.pathname === '/about';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      if (!isAboutPage) {
        const sections = ['home', 'services', 'about', 'contact'];
        for (const id of sections) {
          const el = document.getElementById(id);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= 100 && rect.bottom > 100) {
              setActiveSection(id);
              break;
            }
          }
        }
      }
    };

    if (isAboutPage) setActiveSection('about');
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAboutPage]);

  const handleNavClick = (e, link) => {
    setMobileMenuOpen(false);

    if (link.label === 'Contact') {
      e.preventDefault();
      window.open('https://wa.me/919821149384', '_blank');
      return;
    }

    if (link.label === 'About Us') return;

    if (!isAboutPage) {
      e.preventDefault();
      const id = link.href.replace('#', '');
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderNavLink = (link, mobile = false) => {
    const sectionId = link.href.replace('#', '');
    const isActive = activeSection === sectionId;
    const classes = mobile
      ? `block w-full text-left py-3 text-base font-medium ${
          isActive ? 'text-black' : 'text-gray-600 hover:text-black'
        }`
      : `nav-link text-sm font-medium ${
          isActive ? 'active text-black' : 'text-gray-500 hover:text-black'
        }`;

    if (link.label === 'About Us') {
      return (
        <Link
          key={link.label}
          to="/about"
          className={`${classes} no-underline`}
          onClick={() => setMobileMenuOpen(false)}
        >
          {link.label}
        </Link>
      );
    }

    if (link.label === 'Contact') {
      return (
        <a
          key={link.label}
          href="https://wa.me/919821149384"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => handleNavClick(e, link)}
          className={classes}
        >
          {link.label}
        </a>
      );
    }

    if (isAboutPage) {
      return (
        <Link
          key={link.label}
          to={`/${link.href}`}
          className={`${classes} no-underline`}
          onClick={() => setMobileMenuOpen(false)}
        >
          {link.label}
        </Link>
      );
    }

    return (
      <a
        key={link.label}
        href={link.href}
        onClick={(e) => handleNavClick(e, link)}
        className={classes}
      >
        {link.label}
      </a>
    );
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''} relative`}>
      <div className="container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 no-underline min-w-0">
          <img
            src="https://customer-assets.emergentagent.com/job_site-duplicator-64/artifacts/q1rcdtxx_image.png"
            alt="Mahadev Printers Logo"
            className="h-8 sm:h-10 w-auto object-contain shrink-0"
          />
          <span className="text-base sm:text-lg font-bold text-black truncate">
            Mahadev Printers
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => renderNavLink(link))}
        </div>

        <button
          className="md:hidden flex flex-col justify-center items-center w-10 h-10"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span
            className={`block w-5 h-0.5 bg-black transition-all duration-300 ${
              mobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''
            }`}
          />
          <span
            className={`block w-5 h-0.5 bg-black my-1 transition-all duration-300 ${
              mobileMenuOpen ? 'opacity-0' : ''
            }`}
          />
          <span
            className={`block w-5 h-0.5 bg-black transition-all duration-300 ${
              mobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''
            }`}
          />
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-white border-t border-gray-200 shadow-md z-50">
          <div className="px-4 py-3 flex flex-col">
            {navLinks.map((link) => renderNavLink(link, true))}
          </div>
        </div>
      )}
    </nav>
  );
};

// Scroll Progress Bar
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    setProgress(docHeight > 0 ? scrollTop / docHeight : 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div className="scroll-progress" style={{ transform: `scaleX(${progress})` }} />
  );
};

// Floating WhatsApp Button
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/919821149384?text=Hi%20Mahadev%20Printers%2C%20I%20need%20a%20quote"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat on WhatsApp"
      className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg p-3 sm:p-4 transition-all duration-300 hover:scale-110"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        className="w-6 h-6 sm:w-7 sm:h-7 fill-current"
      >
        <path d="M16.04 3C8.84 3 3 8.74 3 15.82c0 2.5.73 4.93 2.11 7.02L3 29l6.37-2.05a13.2 13.2 0 0 0 6.67 1.82h.01c7.2 0 13.04-5.74 13.04-12.82C29.09 8.74 23.25 3 16.04 3zm0 23.48h-.01a10.9 10.9 0 0 1-5.55-1.52l-.4-.24-3.78 1.21 1.23-3.68-.26-.38a10.53 10.53 0 0 1-1.65-5.65c0-5.85 4.78-10.61 10.67-10.61 2.85 0 5.53 1.1 7.55 3.09a10.45 10.45 0 0 1 3.13 7.52c0 5.85-4.79 10.61-10.68 10.61zm5.86-7.95c-.32-.16-1.89-.92-2.18-1.02-.29-.11-.5-.16-.71.16-.21.32-.82 1.02-1 1.23-.18.21-.36.24-.68.08-.32-.16-1.33-.49-2.53-1.57-.93-.83-1.56-1.85-1.74-2.17-.18-.32-.02-.49.14-.65.14-.14.32-.36.48-.53.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.71-1.71-.97-2.34-.26-.62-.52-.53-.71-.54h-.61c-.21 0-.56.08-.85.4-.29.32-1.12 1.09-1.12 2.66 0 1.57 1.15 3.08 1.31 3.29.16.21 2.25 3.43 5.45 4.81.76.33 1.36.52 1.82.67.76.24 1.45.21 2 .13.61-.09 1.89-.77 2.16-1.51.27-.74.27-1.37.19-1.51-.08-.13-.29-.21-.61-.37z" />
      </svg>
    </a>
  );
};

// Home Page
const HomePage = () => (
  <>
    <HeroSection />
    <TrustStrip />
    <ProductsSection />
    <TestimonialsSection />
    <div id="about">
      <ClientsSection />
    </div>
    <CTASection />
    <Footer />
  </>
);

function App() {
  return (
    <BrowserRouter>
      <div className="bg-white text-black min-h-screen">
        <ScrollProgress />
        <Navbar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
        </Routes>
        <WhatsAppButton />
        <Analytics />
      </div>
    </BrowserRouter>
  );
}

export default App;
